/**
 * Portfolio value line chart showing value over time
 *
 * STATUS: PRESERVED BUT NOT CURRENTLY USED
 * See index.ts for full explanation.
 *
 * This component renders a line chart with portfolio value over time.
 * Shows cost basis reference line and gain/loss tooltips.
 * Potential uses: Admin dashboard, mobile views, email digests.
 */
import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { PortfolioHistoryPoint } from '../../types/analytics';

interface PortfolioValueChartProps {
  data: PortfolioHistoryPoint[];
  height?: number;
  showCostBasis?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
  if (!active || !payload || !payload.length) return null;

  const value = payload.find(p => p.name === 'value')?.value ?? 0;
  const costBasis = payload.find(p => p.name === 'cost_basis')?.value ?? 0;
  const gainLoss = value - costBasis;
  const gainLossPct = costBasis > 0 ? (gainLoss / costBasis) * 100 : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-sm text-gray-500 mb-2">{label}</p>
      <p className="text-sm font-medium">
        Value: <span className="text-gray-900">{formatCurrency(value)}</span>
      </p>
      <p className="text-sm font-medium">
        Cost Basis: <span className="text-gray-500">{formatCurrency(costBasis)}</span>
      </p>
      <p className={`text-sm font-medium ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {gainLoss >= 0 ? '+' : ''}{formatCurrency(gainLoss)} ({gainLossPct >= 0 ? '+' : ''}{gainLossPct.toFixed(1)}%)
      </p>
    </div>
  );
};

export function PortfolioValueChart({
  data,
  height = 300,
  showCostBasis = true,
}: PortfolioValueChartProps) {
  const chartData = useMemo(() => {
    return data.map(point => ({
      ...point,
      dateFormatted: formatDate(point.date),
    }));
  }, [data]);

  // Calculate domain for Y axis with some padding
  const yDomain = useMemo(() => {
    if (!data.length) return [0, 100000];

    const values = data.map(d => d.value);
    const costBases = data.map(d => d.cost_basis);
    const allValues = [...values, ...costBases];

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.1;

    return [Math.max(0, min - padding), max + padding];
  }, [data]);

  // Get cost basis for reference line (use first point as it's typically constant)
  const costBasisValue = data.length > 0 ? data[0].cost_basis : 0;

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="dateFormatted"
          tick={{ fontSize: 12, fill: '#6b7280' }}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
        />
        <YAxis
          domain={yDomain}
          tick={{ fontSize: 12, fill: '#6b7280' }}
          tickFormatter={formatCurrency}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
          width={80}
        />
        <Tooltip content={<CustomTooltip />} />

        {/* Cost basis reference line */}
        {showCostBasis && (
          <ReferenceLine
            y={costBasisValue}
            stroke="#9ca3af"
            strokeDasharray="5 5"
            label={{
              value: 'Cost Basis',
              position: 'right',
              fill: '#9ca3af',
              fontSize: 11,
            }}
          />
        )}

        {/* Portfolio value line */}
        <Line
          type="monotone"
          dataKey="value"
          name="value"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, fill: '#3b82f6' }}
        />

        {/* Cost basis line (hidden, for tooltip data) */}
        {showCostBasis && (
          <Line
            type="monotone"
            dataKey="cost_basis"
            name="cost_basis"
            stroke="transparent"
            dot={false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default PortfolioValueChart;
